// Created by Weverton Couto
// on 23/10/2025
// for Techmatic Solutions
// using Odoo 17.0

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    getEstatisticasDia, 
    getEstatisticasMes, 
    getEstatisticas12Meses,
    getTop5Clinicas,
    getMediaDiaria,
    getVariacaoPeriodo,
    getTodasClinicas,
    getClinicasPorMes
} from '../../data/api/adminApi';
import MetricCard from '../../components/Admin/MetricCard';
import HeaderDashboard from '../../components/Admin/HeaderDashboard';
import GraficoLinha12Meses from '../../components/Admin/GraficoLinha12Meses';
import GraficoBarrasTop5 from '../../components/Admin/GraficoBarrasTop5';
import ClinicasPorMes from '../../components/Admin/ClinicasPorMes';

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Data states
    const [estatisticasDia, setEstatisticasDia] = useState(null);
    const [estatisticasMes, setEstatisticasMes] = useState(null);
    const [estatisticas12Meses, setEstatisticas12Meses] = useState(null);
    const [top5Clinicas, setTop5Clinicas] = useState([]);
    const [mediaDiaria, setMediaDiaria] = useState(0);
    const [variacaoDia, setVariacaoDia] = useState(null);
    const [variacaoMes, setVariacaoMes] = useState(null);
    const [clinicasPorMes, setClinicasPorMes] = useState(null);
    const [loadingClinicasMes, setLoadingClinicasMes] = useState(false);
    
    // Filter states
    const [filtros, setFiltros] = useState({
        data: new Date(),
        ano: new Date().getFullYear(),
        mes: new Date().getMonth(),
        idClinica: null,
        periodo: 'dia'
    });


    const carregarDados = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const periodo = filtros.periodo || 'dia';
            
            // Load all data in parallel
            const promises = [
                getEstatisticasDia(filtros.data, filtros.idClinica),
                getEstatisticasMes(filtros.ano, filtros.mes, filtros.idClinica),
                periodo === '12meses' ? getEstatisticas12Meses(filtros.idClinica) : Promise.resolve(null),
                getTop5Clinicas(periodo, filtros.idClinica),
                getMediaDiaria(periodo, filtros.idClinica),
                getVariacaoPeriodo('dia', 'dia', filtros.idClinica),
                getVariacaoPeriodo('mes', 'mes', filtros.idClinica)
            ];

            const [
                dia,
                mes,
                dozeMeses,
                top5,
                media,
                variacaoD,
                variacaoM
            ] = await Promise.all(promises);

            setEstatisticasDia(dia);
            setEstatisticasMes(mes);
            setEstatisticas12Meses(dozeMeses);
            setTop5Clinicas(top5);
            setMediaDiaria(media);
            setVariacaoDia(variacaoD);
            setVariacaoMes(variacaoM);
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            setError('Erro ao carregar dados do dashboard. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [filtros.data, filtros.ano, filtros.mes, filtros.idClinica, filtros.periodo]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const handleFiltrosChange = useCallback((novosFiltros) => {
        setFiltros(novosFiltros);
    }, []);

    const handleFiltrarClinicas = useCallback(async (ano, mes) => {
        setLoadingClinicasMes(true);
        try {
            const dados = await getClinicasPorMes(ano, mes);
            setClinicasPorMes(dados);
        } catch (err) {
            console.error('Error loading clinics by month:', err);
        } finally {
            setLoadingClinicasMes(false);
        }
    }, []);

    const comparacaoDia = useMemo(() => {
        if (variacaoDia === null || variacaoDia === undefined) return null;
        return variacaoDia >= 0 ? 'vs. ontem' : 'vs. ontem';
    }, [variacaoDia]);

    const comparacaoMes = useMemo(() => {
        if (variacaoMes === null || variacaoMes === undefined) return null;
        return variacaoMes >= 0 ? 'vs. mês anterior' : 'vs. mês anterior';
    }, [variacaoMes]);

    if (loading && !estatisticasDia) {
        return (
            <div className="admin-dashboard">
                <div className="dashboard-header-section">
                    <div className="skeleton-card">
                        <div className="skeleton-line long"></div>
                        <div className="skeleton-line medium"></div>
                    </div>
                </div>
                <div className="metrics-grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="skeleton-card">
                            <div className="skeleton-line short"></div>
                            <div className="skeleton-line long"></div>
                            <div className="skeleton-line medium"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="dashboard-header-section">
                    <h1 className="dashboard-title">Erro</h1>
                    <p className="dashboard-subtitle">{error}</p>
                    <button 
                        className="period-btn active" 
                        onClick={carregarDados}
                        style={{ marginTop: '16px' }}
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <HeaderDashboard
                onFiltrosChange={handleFiltrosChange}
                dataInicial={filtros.data ? filtros.data.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                idClinicaInicial={filtros.idClinica || ''}
                periodoInicial={filtros.periodo || 'dia'}
            />

            {loading && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#6B7280' }}>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    Atualizando dados...
                </div>
            )}

            <div className="metrics-grid">
                <MetricCard
                    titulo="Mensagens Enviadas Hoje"
                    valor={estatisticasDia?.totalGeral || 0}
                    icone="fas fa-calendar-day"
                    variacao={variacaoDia}
                    comparacao={comparacaoDia}
                    cor="primary"
                />
                <MetricCard
                    titulo="Mensagens Enviadas no Mês"
                    valor={estatisticasMes?.totalGeral || 0}
                    icone="fas fa-calendar-alt"
                    variacao={variacaoMes}
                    comparacao={comparacaoMes}
                    cor="success"
                />
                <MetricCard
                    titulo="Média Diária de Envios"
                    valor={mediaDiaria}
                    icone="fas fa-chart-line"
                    variacao={null}
                    comparacao="últimos 30 dias"
                    cor="info"
                />
                <MetricCard
                    titulo="Total 12 Meses"
                    valor={estatisticas12Meses?.totalGeral || 0}
                    icone="fas fa-chart-area"
                    variacao={null}
                    comparacao="últimos 12 meses"
                    cor="warning"
                />
            </div>

            <div className="charts-grid">
                <GraficoLinha12Meses dados={estatisticas12Meses} />
                <GraficoBarrasTop5 dados={top5Clinicas} />
            </div>

            <ClinicasPorMes
                dados={clinicasPorMes}
                loading={loadingClinicasMes}
                onFiltrar={handleFiltrarClinicas}
            />
        </div>
    );
};

export default AdminDashboard;
